import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { t } from '@lingui/macro';
import styled from 'styled-components';
import toColorString from 'polished/lib/color/toColorString';
import color from '@decisiv/design-tokens/lib/color';
import Pencil from '@decisiv/iconix/lib/components/Pencil';
import Trash from '@decisiv/iconix/lib/components/Trash';
import History from '@decisiv/iconix/lib/components/History';

import { Button, Flex, P, Table } from '@decisiv/ui-components';

import normalizeTags from '../../utils/normalizeTags';
import { TableWrapper } from './style';

const MAX_VISIBLE_CODES = 12;

const ExpandToggle = styled.button`
  background: none;
  border: none;
  padding: 0 0.25rem;
  margin-left: 0.25rem;
  color: ${toColorString(color.interaction.pacificOcean)};
  cursor: pointer;
  font-weight: 600;
  font-size: inherit;

  &:hover {
    text-decoration: underline;
  }
`;

function WmiCodesCell({ codes }) {
  const [expanded, setExpanded] = useState(false);
  const overflow = codes.length > MAX_VISIBLE_CODES;
  const visible =
    expanded || !overflow ? codes : codes.slice(0, MAX_VISIBLE_CODES);

  return (
    <span>
      {visible.join(', ')}
      {overflow && !expanded && (
        <ExpandToggle
          type="button"
          onClick={() => setExpanded(true)}
          aria-label={`Show all ${codes.length} WMI codes`}
        >
          … +{codes.length - MAX_VISIBLE_CODES}
        </ExpandToggle>
      )}
      {overflow && expanded && (
        <ExpandToggle
          type="button"
          onClick={() => setExpanded(false)}
          aria-label="Show fewer WMI codes"
        >
          show less
        </ExpandToggle>
      )}
    </span>
  );
}

WmiCodesCell.propTypes = {
  codes: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const ActionTooltip = styled.span`
  position: relative;
  display: inline-block;

  &::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%);
    padding: 4px 8px;
    background: ${toColorString(color.base.charcoal)};
    color: ${toColorString(color.base.snowWhite)};
    border-radius: 2px;
    font-size: 0.75rem;
    white-space: nowrap;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.1s ease-in;
    z-index: 99999;
  }

  &:hover::after {
    opacity: 1;
  }
`;

export default function WmiCodesTable({
  wmiCodes,
  totalRecords,
  onEdit,
  onDelete,
  onHistory,
}) {
  const columns = [
    {
      DataCell: (data) => <span>{data?.rowData?.attributes?.oem_name}</span>,
      title: t`OEM NAME`,
      name: 'oem_name',
    },
    {
      DataCell: (data) => (
        <WmiCodesCell codes={data?.rowData?.attributes?.wmi_codes || []} />
      ),
      title: t`WMI CODES`,
      name: 'wmi_codes',
    },
    {
      DataCell: (data) => (
        <span>{normalizeTags(data?.rowData?.attributes?.tags).join(', ')}</span>
      ),
      title: t`TAGS`,
      name: 'tags',
    },
    {
      DataCell: (data) => (
        <Flex flexDirection="row" style={{ gap: '0.75rem' }}>
          <ActionTooltip data-tooltip={t`Audit history`}>
            <Button
              icon={History}
              variant="ghost"
              size="small"
              aria-label={t`Audit history`}
              onClick={() => onHistory(data?.rowData)}
            />
          </ActionTooltip>
          <ActionTooltip data-tooltip={t`Edit`}>
            <Button
              icon={Pencil}
              variant="ghost"
              size="small"
              aria-label={t`Edit`}
              onClick={() => onEdit(data?.rowData)}
            />
          </ActionTooltip>
          <ActionTooltip data-tooltip={t`Delete`}>
            <Button
              icon={Trash}
              variant="ghost"
              size="small"
              intent="danger"
              aria-label={t`Delete`}
              onClick={() => onDelete(data?.rowData)}
            />
          </ActionTooltip>
        </Flex>
      ),
      title: t`ACTIONS`,
      name: 'actions',
    },
  ];

  return (
    <TableWrapper>
      <Table
        columns={columns}
        data={wmiCodes}
        kind="secondary"
        getRowKey={({ id }) => id}
        footer={() => (
          <Flex flex={1} alignItems="center" justifyContent="flex-start">
            <P>{totalRecords} Items</P>
          </Flex>
        )}
      />
    </TableWrapper>
  );
}

WmiCodesTable.propTypes = {
  wmiCodes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      attributes: PropTypes.shape({
        oem_name: PropTypes.string,
        wmi_codes: PropTypes.arrayOf(PropTypes.string),
        tags: PropTypes.arrayOf(PropTypes.string),
        description: PropTypes.string,
      }),
    }),
  ).isRequired,
  totalRecords: PropTypes.number.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onHistory: PropTypes.func.isRequired,
};
